import { getAuth, signInWithCustomToken, signOut } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { app } from './firebase';

const KIOSK_SESSION_KEY = 'maru_kiosk_session';

export interface KioskSession {
  classCode: string;
  pin: string;
  schoolId: string;
  classId: string;
  grade: number;
  classNumber: number;
  connectedAt: number;
}

export interface ClassAccessContext {
  mode: 'teacher' | 'kiosk';
  schoolId: string;
  classId: string;
  grade: number;
  classNumber: number;
  actorUid: string;
}

interface VerifyKioskResponse {
  token: string;
  schoolId: string;
  classId: string;
  grade: number;
  classNumber: number;
  classCode: string;
}

export const getStoredKioskSession = (): KioskSession | null => {
  try {
    const raw = localStorage.getItem(KIOSK_SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as KioskSession;
    if (!parsed.classCode || !parsed.pin || !parsed.schoolId || !parsed.classId) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
};

export const saveKioskSession = (session: KioskSession): void => {
  localStorage.setItem(KIOSK_SESSION_KEY, JSON.stringify(session));
};

export const clearKioskSession = (): void => {
  localStorage.removeItem(KIOSK_SESSION_KEY);
};

export const connectKioskWithClassCode = async (
  classCode: string,
  pin: string
): Promise<KioskSession> => {
  const functions = getFunctions(app);
  const verify = httpsCallable<{ classCode: string; pin: string }, VerifyKioskResponse>(
    functions,
    'verifyClassKioskPin'
  );
  const result = await verify({ classCode: classCode.trim().toUpperCase(), pin: pin.trim() });
  const data = result.data;

  const auth = getAuth(app);
  if (auth.currentUser) {
    await signOut(auth);
  }
  await signInWithCustomToken(auth, data.token);

  const session: KioskSession = {
    classCode: data.classCode,
    pin: pin.trim(),
    schoolId: data.schoolId,
    classId: data.classId,
    grade: data.grade,
    classNumber: data.classNumber,
    connectedAt: Date.now()
  };
  saveKioskSession(session);
  return session;
};

export const restoreKioskSession = async (): Promise<KioskSession | null> => {
  const session = getStoredKioskSession();
  if (!session) return null;

  try {
    await connectKioskWithClassCode(session.classCode, session.pin);
    return session;
  } catch (err) {
    console.error('키오스크 세션 복원 실패:', err);
    clearKioskSession();
    return null;
  }
};

export const disconnectKiosk = async (): Promise<void> => {
  clearKioskSession();
  const auth = getAuth(app);
  if (auth.currentUser) {
    await signOut(auth);
  }
};

export const setClassKioskPin = async (args: {
  schoolId: string;
  classId: string;
  pin: string;
}): Promise<void> => {
  const functions = getFunctions(app);
  const setPin = httpsCallable(functions, 'setClassKioskPin');
  await setPin(args);
};

export const getKioskConnectUrl = (): string => `${window.location.origin}/class/kiosk/connect`;
